import { gql, useApolloClient } from '@apollo/client';
import compact from 'lodash/compact';
import maxBy from 'lodash/maxBy';
import { useCallback, useMemo } from 'react';
import {
  GetStopDetailsDocument,
  GetStopDetailsQuery,
  GetStopDetailsQueryVariables,
  StopRegistryQuayInput,
  StopRegistryStopPlaceInterface,
  StopsDatabaseStopPlaceNewestVersionBoolExp,
  useGetStopDetailsQuery,
} from '../../../../generated/graphql';
import { useObservationDateQueryParam, useUrlQuery } from '../../../../hooks';
import {
  EnrichedQuay,
  EnrichedStopPlace,
  Quay,
  StopPlace,
  StopWithDetails,
} from '../../../../types';
import { Priority } from '../../../../types/enums';
import {
  KnownValueKey,
  findKeyValue,
  getGeometryPoint,
  getStopPlaceDetailsForEnrichment,
  getStopPlacesFromQueryResult,
  useRequiredParams,
} from '../../../../utils';
import { isDateInRange, parseDate } from '../../../../utils/time';
import {
  GetUserNameById,
  useGetUserNames,
} from '../../../common/ChangeHistory';
import {
  getMirrorParentId,
  isMirrorChild,
  mapToEnrichedQuay,
} from '../../Utils';
import { useGetLatestQuayChange } from './useGetQuayChangeHistory';

const GQL_SCHEDULED_STOP_POINT_DETAIL_FIELDS = gql`
  fragment ScheduledStopPointDetailFields on service_pattern_scheduled_stop_point {
    priority
    direction
    scheduled_stop_point_id
    label
    timing_place_id
    timing_place {
      timing_place_id
      label
    }
    validity_start
    validity_end
    located_on_infrastructure_link_id
    stop_place_ref
    measured_location
    vehicle_mode_on_scheduled_stop_point {
      vehicle_mode
    }
  }
`;

const GQL_GET_STOP_DETAILS = gql`
  query GetStopDetails(
    $where: stops_database_stop_place_newest_version_bool_exp
  ) {
    stopsDb: stops_database {
      newestVersion: stops_database_stop_place_newest_version(where: $where) {
        id
        TiamatStopPlace {
          ...StopPlaceDetails
          ... on stop_registry_StopPlace {
            quays {
              ...QuayDetails
            }
          }
        }
      }
    }
  }
`;

const GQL_SHELTER_EQUIPMENT_DETAILS = gql`
  fragment ShelterEquipmentDetails on stop_registry_ShelterEquipment {
    id
    version

    enclosed
    stepFree
    shelterNumber
    shelterType
    shelterElectricity
    shelterLighting
    shelterCondition
    timetableCabinets
    trashCan
    shelterHasDisplay
    bicycleParking
    leaningRail
    outsideBench
    shelterFasciaBoardTaping
    shelterExternalId
  }
`;

const GQL_HSL_ACCESSIBILITY_ASSESSMENT_DETAILS = gql`
  fragment AccessibilityAssessmentDetails on stop_registry_AccessibilityAssessment {
    id
    version

    hslAccessibilityProperties {
      ...HslAccessibilityPropertiesDetails
    }
    limitations {
      id
      version

      audibleSignalsAvailable
      escalatorFreeAccess
      liftFreeAccess
      stepFreeAccess
      visualSignsAvailable
      wheelchairAccess
    }
  }
`;

const GQL_QUAY_DETAILS = gql`
  fragment QuayDetails on stop_registry_Quay {
    id
    version

    publicCode
    privateCode {
      type
      value
    }
    description {
      lang
      value
    }
    alternativeNames {
      name {
        lang
        value
      }
      nameType
    }
    geometry {
      coordinates
      type
    }
    accessibilityAssessment {
      ...AccessibilityAssessmentDetails
    }
    keyValues {
      key
      values
    }
    infoSpots {
      ...InfoSpotDetails
    }
    placeEquipments {
      id
      shelterEquipment {
        ...ShelterEquipmentDetails
      }
      cycleStorageEquipment {
        id
        version

        cycleStorageType
      }
      generalSign {
        id
        version

        privateCode {
          value
          type
        }
        content {
          value
        }
        signContentType
        numberOfFrames
        replacesRailSign
        note {
          lang
          value
        }
      }
    }
    scheduled_stop_point {
      ...ScheduledStopPointDetailFields
    }
    externalLinks {
      ...ExternalLinksDetails
    }
    organisations {
      relationshipType
      organisationRef
      organisation {
        ...StopPlaceOrganisationFields
      }
    }
  }
`;

const GQL_EXTERNAL_LINKS_DETAILS = gql`
  fragment ExternalLinksDetails on stop_registry_externalLink {
    quayId
    orderNum
    name
    location
  }
`;

const GQL_TOPOGRAPHIC_PLACE_DETAILS = gql`
  fragment TopographicPlaceDetails on stop_registry_TopographicPlace {
    id
    version

    topographicPlaceType
    name {
      value
      lang
    }
  }
`;

const GQL_FARE_ZONE_DETAILS = gql`
  fragment FareZoneDetails on stop_registry_FareZone {
    id
    version

    name {
      value
      lang
    }
  }
`;

const GQL_HSL_ACCESSIBILITY_PROPERTIES_DETAILS = gql`
  fragment HslAccessibilityPropertiesDetails on stop_registry_HslAccessibilityProperties {
    id
    version

    stopAreaSideSlope
    stopAreaLengthwiseSlope
    endRampSlope
    shelterLaneDistance
    curbBackOfRailDistance
    curbDriveSideOfRailDistance
    structureLaneDistance
    stopElevationFromRailTop
    stopElevationFromSidewalk
    lowerCleatHeight
    serviceAreaWidth
    serviceAreaLength
    platformEdgeWarningArea
    guidanceTiles
    guidanceStripe
    serviceAreaStripes
    sidewalkAccessibleConnection
    stopAreaSurroundingsAccessible
    curvedStop
    stopType
    shelterType
    guidanceType
    mapType
    pedestrianCrossingRampType
    accessibilityLevel
  }
`;

const GQL_STOP_PLACE_ORGANISATION_FIELDS = gql`
  fragment StopPlaceOrganisationFields on stop_registry_Organisation {
    id
    version

    name
    privateContactDetails {
      id
      version

      email
      phone
    }
  }
`;

const GQL_INFO_SPOT_DETAILS = gql`
  fragment InfoSpotDetails on stop_registry_infoSpot {
    id
    version

    backlight
    description {
      lang
      value
    }
    displayType
    floor
    label
    width
    height
    infoSpotLocations
    infoSpotType
    intendedUser
    railInformation
    speechProperty
    zoneLabel
    keyValues {
      key
      values
    }
    poster {
      ...PosterDetails
    }
    geometry {
      type
      coordinates
    }
  }
`;

const GQL_POSTER_DETAILS = gql`
  fragment PosterDetails on stop_registry_poster {
    id
    version

    label
    width
    height
    lines
    keyValues {
      key
      values
    }
  }
`;

function getEnrichedStopPlace(
  stopPlace: StopPlace | null,
): EnrichedStopPlace | null {
  if (!stopPlace) {
    return null;
  }

  const transformedStopPlace = {
    ...stopPlace,
    parentStopPlace: stopPlace.parentStopPlace
      ? [stopPlace.parentStopPlace as StopRegistryStopPlaceInterface]
      : undefined,
  };

  return {
    ...stopPlace,
    ...getStopPlaceDetailsForEnrichment(transformedStopPlace),
  };
}

function validOn(observationDate: Temporal.PlainDate) {
  return (quay: Quay) => {
    const validityStart = quay.scheduled_stop_point?.validity_start;
    const validityEnd = quay.scheduled_stop_point?.validity_end;

    if (!validityStart) {
      return false;
    }

    return isDateInRange(observationDate, validityStart, validityEnd);
  };
}

function getCorrectQuay(
  quays: ReadonlyArray<Quay | null | undefined> | null | undefined,
  requestedPublicCode: string,
  observationDate: Temporal.PlainDate,
  priority: number,
): Quay | null {
  const validQuays = compact(quays)
    .filter((it) => it.publicCode === requestedPublicCode)
    .filter((it) => !isMirrorChild(it))
    .filter(validOn(observationDate));

  if (Number.isFinite(priority)) {
    return (
      validQuays.find(
        (quay) =>
          findKeyValue(quay, KnownValueKey.Priority) === String(priority),
      ) ?? null
    );
  }

  // Ignore drafts by default. Only show if requested separately by priority query param.
  // This is to align the behaviour of the page with how the map works.
  const nonDraftQuays = validQuays.filter(
    (quay) =>
      Number(findKeyValue(quay, KnownValueKey.Priority)) !== Priority.Draft,
  );
  return (
    maxBy(nonDraftQuays, (quay) =>
      Number(findKeyValue(quay, KnownValueKey.Priority)),
    ) ?? null
  );
}

function getStopDetails(
  data: GetStopDetailsQuery | undefined,
  observationDate: Temporal.PlainDate,
  priority: number,
  label: string,
  getUserNameById: GetUserNameById,
  quayChangeData?: {
    changed: string | null;
    changedBy: string | null;
  },
): StopWithDetails | null {
  const stopPlaceResults = data?.stopsDb?.newestVersion ?? [];

  const result = compact(
    stopPlaceResults.map((stopPlaceResult) => {
      const [stopPlace] = getStopPlacesFromQueryResult<StopPlace>(
        stopPlaceResult.TiamatStopPlace,
      );

      if (!stopPlace) {
        return null;
      }

      const selectedQuay = getCorrectQuay(
        stopPlace.quays,
        label,
        observationDate,
        priority,
      );

      const stopPoint = selectedQuay?.scheduled_stop_point;

      return stopPoint ? { stopPoint, stopPlace, selectedQuay } : null;
    }),
  ).at(0);

  if (!result) {
    return null;
  }

  const changeData = quayChangeData;
  const changedByUserName = getUserNameById(changeData?.changedBy);

  return {
    ...result.stopPoint,
    location: getGeometryPoint(result.stopPoint.measured_location),
    stop_place: getEnrichedStopPlace(result.stopPlace),
    quay: mapToEnrichedQuay(
      result.selectedQuay,
      result.stopPlace?.accessibilityAssessment,
      changeData?.changed,
      changedByUserName,
    ),
  };
}

export type MirroredQuayDetails = {
  readonly quay: EnrichedQuay;
  readonly stopPlace: EnrichedStopPlace;
};

function getMirroredQuays(
  data: GetStopDetailsQuery | undefined,
  parentQuay: Quay | null,
): ReadonlyArray<MirroredQuayDetails> {
  if (!parentQuay?.id) {
    return [];
  }

  const parentQuayId = parentQuay.id;
  const stopPlaceResults = data?.stopsDb?.newestVersion ?? [];

  return compact(
    stopPlaceResults.flatMap((result) => {
      const [stopPlace] = getStopPlacesFromQueryResult<StopPlace>(
        result.TiamatStopPlace,
      );
      if (!stopPlace) {
        return [];
      }

      const enrichedStopPlace = getEnrichedStopPlace(stopPlace);
      if (!enrichedStopPlace) {
        return [];
      }

      return compact(stopPlace.quays)
        .filter((q) => getMirrorParentId(q) === parentQuayId)
        .map((quay) => {
          const enrichedQuay = mapToEnrichedQuay(
            quay,
            stopPlace.accessibilityAssessment,
          );
          if (!enrichedQuay) {
            return null;
          }
          return { quay: enrichedQuay, stopPlace: enrichedStopPlace };
        });
    }),
  );
}

function getWhereCondition(
  label: string,
): StopsDatabaseStopPlaceNewestVersionBoolExp {
  // Find the stop place that contains a quay with this public code
  return {
    stop_place_quays: {
      quay: {
        public_code: { _eq: label },
      },
    },
  };
}

export function useGetStopDetails() {
  const { label } = useRequiredParams<{ label: string }>();
  const { observationDate } = useObservationDateQueryParam();
  const { queryParams } = useUrlQuery();
  const priority = Number(queryParams.priority);
  const getUserNameById = useGetUserNames();

  const where = getWhereCondition(label);
  const { data, ...rest } = useGetStopDetailsQuery({ variables: { where } });

  const { latestQuayChangeData } = useGetLatestQuayChange({
    public_code: { _eq: label },
  });

  const observationDateStr = observationDate.toString();
  const stopDetails = useMemo(
    () =>
      getStopDetails(
        data,
        parseDate(observationDateStr),
        priority,
        label,
        getUserNameById,
        latestQuayChangeData,
      ),
    [
      data,
      observationDateStr,
      priority,
      label,
      getUserNameById,
      latestQuayChangeData,
    ],
  );

  const mirroredQuays = useMemo(
    () => getMirroredQuays(data, stopDetails?.quay ?? null),
    [data, stopDetails?.quay],
  );

  return { ...rest, stopDetails, mirroredQuays };
}

export function useGetStopDetailsLazy() {
  const apollo = useApolloClient();
  const getUserNameById = useGetUserNames();

  return useCallback(
    async (
      label: string,
      observationDate: Temporal.PlainDate,
      priority: number,
      quayChangeData?: {
        changed: string | null;
        changedBy: string | null;
      },
    ) => {
      const where = getWhereCondition(label);
      const { data, ...rest } = await apollo.query<
        GetStopDetailsQuery,
        GetStopDetailsQueryVariables
      >({ query: GetStopDetailsDocument, variables: { where } });

      const stopDetails = getStopDetails(
        data,
        observationDate,
        priority,
        label,
        getUserNameById,
        quayChangeData,
      );

      return { ...rest, stopDetails };
    },
    [getUserNameById, apollo],
  );
}

export function getQuayIdsFromStopExcept(
  stop: StopWithDetails | null,
  quayId: string | null | undefined,
): Array<StopRegistryQuayInput> {
  return (
    stop?.stop_place?.quays
      ?.map((quay) => quay?.id)
      .filter((id) => id !== quayId)
      .map((id) => ({ id })) ?? []
  );
}
